import { useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { LockKeyOpen, Check, X, Clock } from '@phosphor-icons/react';
import api from '../utils/api';
import { toast } from 'sonner';

const to12h = (t) => {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`;
};

export const EditRequestsPanel = ({ branchId }) => {
  const [requests, setRequests] = useState([]);
  const [busy, setBusy] = useState(null);
  const [showHistory, setShowHistory] = useState(false);

  const load = useCallback(async () => {
    if (!branchId) return;
    try {
      const res = await api.get('/edit-requests', { params: { branch_id: branchId } });
      setRequests(res.data.requests || []);
    } catch (e) {
      // approver may lack access; keep quiet
    }
  }, [branchId]);

  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, [load]);

  const review = async (req, action) => {
    try {
      setBusy(req.request_id);
      await api.post(`/edit-requests/${req.request_id}/${action}`);
      toast.success(action === 'approve' ? `Edit access granted to ${req.requested_by_name} for ${req.customer_name}` : 'Request rejected');
      load();
      window.dispatchEvent(new Event('edit-requests-changed'));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update request');
    } finally {
      setBusy(null);
    }
  };

  const pending = requests.filter((r) => r.status === 'pending');
  const history = requests.filter((r) => r.status !== 'pending').slice(0, 10);

  return (
    <Card className="p-6 bg-white border-[#E5E5DF]" data-testid="edit-requests-panel">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-medium text-[#1C1C1A] flex items-center gap-2">
          <LockKeyOpen size={22} className="text-[#C05C42]" weight="fill" />
          Edit Requests
          {pending.length > 0 && (
            <span data-testid="edit-requests-pending-count" className="ml-1 text-xs font-bold bg-[#C05C42] text-white rounded-full px-2 py-0.5">{pending.length}</span>
          )}
        </h2>
        <button type="button" onClick={() => setShowHistory((v) => !v)} className="text-xs text-[#2C4C3B] underline">
          {showHistory ? 'Hide history' : 'Show history'}
        </button>
      </div>
      {pending.length === 0 ? (
        <p className="text-center py-6 text-[#1C1C1A]/40 text-sm" data-testid="edit-requests-empty">No pending edit requests for this branch</p>
      ) : (
        <div className="space-y-2">
          {pending.map((req) => (
            <div key={req.request_id} data-testid={`edit-request-${req.request_id}`} className="flex flex-col sm:flex-row sm:items-center gap-3 p-3 border border-amber-200 bg-amber-50/60 rounded-md">
              <Clock size={20} className="text-amber-600 shrink-0" />
              <div className="flex-1 text-sm text-[#1C1C1A]">
                <span className="font-semibold">{req.requested_by_name}</span> requested edit access for{' '}
                <span className="font-semibold">{req.customer_name}</span>
                {req.customer_reg_no && <span className="text-[#1C1C1A]/60"> (Reg {req.customer_reg_no})</span>}
                {req.batch_time && <> in <span className="font-medium">{to12h(req.batch_time)}</span> batch</>}
                <span className="block text-xs text-[#1C1C1A]/50 mt-0.5">{req.date} · {new Date(req.created_at).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</span>
              </div>
              <div className="flex gap-2">
                <Button size="sm" data-testid={`approve-edit-request-${req.request_id}`} disabled={busy === req.request_id} onClick={() => review(req, 'approve')} className="bg-[#2C4C3B] hover:bg-[#1F3529] text-white">
                  <Check size={14} className="mr-1" /> Approve
                </Button>
                <Button size="sm" variant="outline" data-testid={`reject-edit-request-${req.request_id}`} disabled={busy === req.request_id} onClick={() => review(req, 'reject')} className="border-red-300 text-red-700 hover:bg-red-50">
                  <X size={14} className="mr-1" /> Reject
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
      {showHistory && history.length > 0 && (
        <div className="mt-4 border-t border-[#E5E5DF] pt-3 space-y-1">
          {history.map((r) => (
            <p key={r.request_id} className="text-xs text-[#1C1C1A]/70">
              <span className={`font-semibold ${r.status === 'approved' ? 'text-green-700' : 'text-red-700'}`}>{r.status.toUpperCase()}</span> · {r.requested_by_name} → {r.customer_name} ({r.date}){r.reviewed_by_name ? ` by ${r.reviewed_by_name}` : ''}
            </p>
          ))}
        </div>
      )}
    </Card>
  );
};

// Small control rendered inside a completed bed tile
export const RequestEditControl = ({ status, onRequest, onOpen }) => {
  if (status === 'approved') {
    return (
      <Button size="sm" data-testid="edit-unlocked-btn" onClick={(e) => { e.stopPropagation(); onOpen(); }} className="w-full mt-2 bg-[#2C4C3B] hover:bg-[#1F3529] text-white text-[10px] sm:text-xs py-1 h-auto">
        <LockKeyOpen size={12} weight="fill" className="mr-1" /> Edit Unlocked
      </Button>
    );
  }
  if (status === 'pending') {
    return (
      <span data-testid="edit-request-pending-badge" className="inline-flex items-center gap-1 mt-2 text-[10px] font-medium text-amber-700 bg-amber-100 border border-amber-300 rounded px-1.5 py-0.5">
        <Clock size={10} /> Requested
      </span>
    );
  }
  return (
    <Button size="sm" variant="outline" data-testid="request-edit-btn" onClick={(e) => { e.stopPropagation(); onRequest(); }} className="w-full mt-2 border-[#C05C42] text-[#C05C42] hover:bg-[#C05C42] hover:text-white text-[10px] sm:text-xs py-1 h-auto">
      Request Edit
    </Button>
  );
};
